// src/pages/Reports.jsx
import React, { useState } from 'react';
import { 
  Box, Button, Typography, Grid, Card, 
  Tabs, Tab, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Paper,
  FormControl, InputLabel, Select, MenuItem,
  LinearProgress
} from '@mui/material';
import { 
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  GridOn as ExcelIcon,
  TableChart as CsvIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  ShowChart as LineChartIcon
} from '@mui/icons-material';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const reportTypes = [
  { id: 'donations', label: 'Donations Summary' },
  { id: 'volunteers', label: 'Volunteer Participation' },
  { id: 'events', label: 'Event Performance' },
  { id: 'expenses', label: 'Expense Summary' },
  { id: 'donors', label: 'Top Donors' },
];

const COLORS = ['#00b894', '#0984e3', '#6c5ce7', '#fdcb6e', '#e17055'];

// Mock data for reports
const donationsData = [
  { month: 'Jan', amount: 120000 },
  { month: 'Feb', amount: 190000 },
  { month: 'Mar', amount: 150000 },
  { month: 'Apr', amount: 220000 },
  { month: 'May', amount: 180000 },
  { month: 'Jun', amount: 250000 },
];

const volunteersData = [
  { name: 'Food Distribution', volunteers: 24 },
  { name: 'Education Camp', volunteers: 18 },
  { name: 'Healthcare', volunteers: 32 },
  { name: 'Environment', volunteers: 15 },
  { name: 'Fundraising', volunteers: 28 },
];

const eventsData = [
  { name: 'Food Drive', target: 500000, raised: 420000 },
  { name: 'Education Fund', target: 800000, raised: 650000 },
  { name: 'Medical Camp', target: 300000, raised: 280000 },
  { name: 'Tree Plantation', target: 200000, raised: 180000 },
];

const expensesData = [
  { category: 'Programs', amount: 420000 },
  { category: 'Administration', amount: 120000 },
  { category: 'Fundraising', amount: 80000 },
  { category: 'Marketing', amount: 50000 },
];

const topDonorsData = [
  { name: 'John Smith', amount: 150000, donations: 5 },
  { name: 'Sarah Johnson', amount: 120000, donations: 3 },
  { name: 'Michael Brown', amount: 90000, donations: 7 },
  { name: 'Emily Davis', amount: 75000, donations: 4 },
  { name: 'Robert Wilson', amount: 60000, donations: 6 },
];

const Reports = () => {
  const [activeReport, setActiveReport] = useState('donations');
  const [timeFrame, setTimeFrame] = useState('last6months');
  const [chartType, setChartType] = useState('bar');
  const [exportFormat, setExportFormat] = useState('pdf');
  const [loading, setLoading] = useState(false);

  const handleChangeReport = (event, newValue) => {
    setActiveReport(newValue);
  };

  const handleTimeFrameChange = (e) => {
    setTimeFrame(e.target.value);
  };

  const handleChartTypeChange = (e) => {
    setChartType(e.target.value);
  };

  const handleExportFormatChange = (e) => {
    setExportFormat(e.target.value);
  };

  const handleGenerateReport = () => {
    setLoading(true);
    // Simulate report generation
    setTimeout(() => {
      setLoading(false);
      alert(`Report exported as ${exportFormat.toUpperCase()} successfully!`);
    }, 1500);
  };

  const renderReportData = () => {
    switch (activeReport) {
      case 'donations':
        return donationsData;
      case 'volunteers':
        return volunteersData;
      case 'events':
        return eventsData;
      case 'expenses':
        return expensesData;
      case 'donors':
        return topDonorsData;
      default:
        return donationsData;
    }
  };

  const renderChart = () => {
    const data = renderReportData();
    
    if (chartType === 'pie') {
      return (
        <ResponsiveContainer width="100%" height={400}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={true}
              outerRadius={150}
              fill="#8884d8"
              dataKey={activeReport === 'expenses' ? 'amount' : activeReport === 'volunteers' ? 'volunteers' : 'raised' || 'amount'}
              nameKey={activeReport === 'volunteers' ? 'name' : 'month' || 'category' || 'name'}
              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(value) => [`₹${value.toLocaleString()}`, 'Amount']} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      );
    }
    
    return (
      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={activeReport === 'volunteers' ? 'name' : 'month' || 'category' || 'name'} />
          <YAxis />
          <Tooltip formatter={(value) => [`₹${value.toLocaleString()}`, 'Amount']} />
          <Legend />
          <Bar 
            dataKey={activeReport === 'volunteers' ? 'volunteers' : activeReport === 'events' ? 'raised' : 'amount'} 
            fill="#00b894" 
            name={activeReport === 'volunteers' ? 'Volunteers' : 'Amount'} 
          />
          {activeReport === 'events' && (
            <Bar dataKey="target" fill="#0984e3" name="Target" />
          )}
        </BarChart>
      </ResponsiveContainer>
    );
  };

  const renderDataTable = () => {
    const data = renderReportData();
    
    return (
      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              {Object.keys(data[0]).map(key => (
                <TableCell key={key} sx={{ fontWeight: 600 }}>
                  {key.charAt(0).toUpperCase() + key.slice(1)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, index) => (
              <TableRow key={index}>
                {Object.values(row).map((value, idx) => (
                  <TableCell key={idx}>
                    {typeof value === 'number' ? `₹${value.toLocaleString()}` : value}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>Reports</Typography>
      
      <Card sx={{ mb: 3 }}>
        <Tabs 
          value={activeReport} 
          onChange={handleChangeReport}
          variant="scrollable"
          scrollButtons="auto"
        >
          {reportTypes.map(report => (
            <Tab 
              key={report.id} 
              label={report.label} 
              value={report.id} 
              icon={<BarChartIcon />}
            />
          ))}
        </Tabs>
      </Card>
      
      <Card sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Time Frame</InputLabel>
              <Select
                value={timeFrame}
                onChange={handleTimeFrameChange}
                label="Time Frame"
              >
                <MenuItem value="lastweek">Last Week</MenuItem>
                <MenuItem value="lastmonth">Last Month</MenuItem>
                <MenuItem value="lastquarter">Last Quarter</MenuItem>
                <MenuItem value="last6months">Last 6 Months</MenuItem>
                <MenuItem value="lastyear">Last Year</MenuItem>
                <MenuItem value="alltime">All Time</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Chart Type</InputLabel>
              <Select
                value={chartType}
                onChange={handleChartTypeChange}
                label="Chart Type"
              >
                <MenuItem value="bar">Bar Chart</MenuItem>
                <MenuItem value="pie">Pie Chart</MenuItem>
                <MenuItem value="line">Line Chart</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Export Format</InputLabel>
              <Select
                value={exportFormat}
                onChange={handleExportFormatChange}
                label="Export Format"
              >
                <MenuItem value="pdf">PDF</MenuItem>
                <MenuItem value="excel">Excel</MenuItem>
                <MenuItem value="csv">CSV</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Button 
              variant="contained" 
              fullWidth
              startIcon={<DownloadIcon />}
              onClick={handleGenerateReport}
              disabled={loading}
              sx={{ height: '56px' }}
            >
              {loading ? 'Generating...' : 'Generate Report'}
            </Button>
          </Grid>
        </Grid>
      </Card>
      
      {loading ? (
        <Card sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Generating Report...</Typography>
          <LinearProgress />
        </Card>
      ) : (
        <>
          <Card sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" sx={{ mb: 3 }}>
              {reportTypes.find(r => r.id === activeReport)?.label}
            </Typography>
            {renderChart()}
          </Card>
          
          <Card sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 3 }}>
              Detailed Data
            </Typography>
            {renderDataTable()}
          </Card>
        </>
      )}
    </Box>
  );
};

export default Reports;